<template>
    <div class="equipment-list">
        <heading title="Rental Equipment List" tag="rental-equipment-list-title">
            <export-button @export="exportList" :fields="exportFields" profile-key="equipment">
                Export
            </export-button>
        </heading>

        <data-table ref="dataTable"
                    persist-name="rental-equipment-list"
                    :show-filters="true"
                    :show-pager="true"
                    :get-items="getEntries"
                    v-model="filters"
                    :fields="fields"
                    @multiselect-item-selected="multiselectFilterChanged">
            <!-- template for custom columns -->
            <template v-slot:customField="slotProps">
                <div v-if="slotProps.row.field.key === 'actions'" class="action-buttons d-flex">
                    <router-link class="btn btn-sm btn-secondary mr-2 btn-icon"
                                 target="_blank"
                                 :to="`/equipment/view/${slotProps.row.item.id}`">
                        <b-icon-eye/>
                    </router-link>
                </div>
                <div v-else-if="slotProps.row.field.key === 'type'">
                    {{ getType(slotProps.row.item) }}
                </div>
                <div v-else-if="slotProps.row.field.key === 'company'">
                    {{ slotProps.row.item.company['name'] }}
                </div>
                <div v-else-if="slotProps.row.field.key === 'attestation'">
                    <attestation-badge :equipment="slotProps.row.item"/>
                </div>
                <div v-else-if="slotProps.row.field.key === 'currentCompliance'">
                    <compliance-badge :compliance="slotProps.row.item.currentCompliance"
                                      v-if="isAttested(slotProps.row.item)"/>
                </div>
                <div v-else-if="slotProps.row.field.key === 'registration'">
                    <registration-tag-badge :equipment="slotProps.row.item"></registration-tag-badge>
                </div>
                <div v-else-if="slotProps.row.field.key === 'plan'">
                    <compliance-plan-badge :equipment="slotProps.row.item"
                                           v-if="isAttested(slotProps.row.item) && isNoncompliant(slotProps.row.item)"/>
                </div>
                <div v-else-if="slotProps.row.field.key === 'created'">
                    <timestamp :include-time="false"
                               :include-year="true"
                               :date="slotProps.row.item.created"/>
                </div>
            </template>
        </data-table>
    </div>
</template>

<script>
import Vue from 'vue'
import * as _ from 'lodash'
import Export from '@/providers/Export'
import Equipment from '@/models/Equipment'
import Api from '@/providers/Api'
import RegistrationTagBadge from '@/components/RegistrationTagBadge'

export default Vue.extend({
    name: 'EquipmentList',
    components: {RegistrationTagBadge},
    data() {
        return {
            types: [],
            equipmentFields: Equipment,
            fields: [
                // { key: 'id', sortable: true, label: 'ID' },
                {key: 'registrationNumber', sortable: false, label: 'Registration #'},
                {key: 'vehicle_id', sortable: true, label: 'Vehicle ID/Name'},
                {key: 'vin', sortable: true, label: 'VIN'},
                {key: 'type', sortable: false, label: 'Type'},
                // { key: 'equipment_model', sortable: true, label: 'Model' },
                {key: 'company', sortable: true, label: 'Rental Company'},
                // { key: 'updated', sortable: true, label: 'Last Updated' },
                {key: 'attestation', sortable: false, label: 'Equipment Status'},
                {key: 'currentCompliance', sortable: false, label: 'Current Compliance'},
                {key: 'registration', sortable: false, label: 'Registration Tag'},
                {key: 'plan', sortable: false, label: 'Compliance Plan'},
                {key: 'actions', sortable: false},
            ],
            exportFields: {
                registrationNumber: 'Registration #',
                vehicle_id: 'Vehicle ID',
                vin: 'VIN',
                type: 'Type',
                fuel_type: 'Fuel Type',
                on_road: 'On-road',
                company: 'Company',
                cobid_certified: 'COBID Certified',
                attested: 'Attested',
                emissionTier: 'Emission Tier',
                exemption_type: 'Exemption Type',
                exemption_granted_thru: 'Exemption Granted Through',

                // these must be updated yearly along with ComplianceService::$YEARS
                compliance_2024: '2024 Compliance',
                compliance_2025: '2025 Compliance',
                compliance_2026: '2026 Compliance',
                compliance_2027: '2027 Compliance',

                compliance_plan: 'Compliance Plan',
                equipment_model_year: 'Equipment Model Year',
                equipment_manufacturer: 'Equipment Manufacturer',
                equipment_model: 'Equipment Model',
                equipment_serial_number: 'Equipment Serial Number',
                engine_family_name: 'Engine Family Name',
                engine_manufacturer: 'Engine Manufacturer',
                engine_model: 'Engine Model',
                engine_model_year: 'Engine Model Year',
                engine_serial_number: 'Engine Serial Number',
                engine_hp: 'Engine HP',
                userLogs: 'Notes'
            },
            selectedEquipment: [],
        }
    },
    computed: {
        filters() {
            return {
                rental: {
                    field: 'rental', type: 'hidden', value: 'RENTAL', label: 'Equipment Type'
                },
                registrationNumber: {
                    field: 'registrationNumber',
                    type: 'string',
                    value: null,
                    label: 'Registration #'
                },
                vehicle_id: {field: 'vehicle_id', type: 'string', value: null, label: 'Vehicle ID/Name'},
                on_road: {
                    field: 'on_road',
                    type: 'boolean',
                    value: null,
                    label: 'On Road',
                },
                attestationStatus: {
                    field: 'attestationStatus', type: 'select', value: null, label: 'Equipment Status',
                    options: [
                        {value: 'ATTESTATION_STATUS_ATTESTED', text: 'Attested'},
                        {value: 'ATTESTATION_STATUS_UPDATED', text: 'Non-Attested'},
                        {value: 'ATTESTATION_STATUS_INCOMPLETE', text: 'Incomplete'},
                    ]
                },
                currentCompliance: {
                    field: 'currentCompliance', type: 'select', value: null, label: 'Current Compliance',
                    options: [
                        {value: 'NA', text: 'N/A'},
                        {value: 'NONCOVERED', text: 'Non-Covered'},
                        {value: 'COMPLIANT', text: 'Compliant'},
                        {value: 'NONCOMPLIANT', text: 'Noncompliant'},
                    ]
                },
                tagDisplay: {
                    field: 'tagDisplay', type: 'select', value: null, label: 'Registration Tag',
                    options: [
                        {value: 'A', text: 'A'},
                        {value: '2022', text: '2022'},
                        {value: '2023', text: '2023'},
                        {value: '2024', text: '2024'},
                        {value: '2025', text: '2025'},
                        {value: '2026', text: '2026'},
                        {value: '2027', text: '2027'},
                    ]
                },
                compliance_plan: {
                    field: 'compliance_plan', type: 'select', value: null, label: 'Compliance Plan',
                    options: [
                        {value: 'REPOWER', text: 'Repower'},
                        {value: 'RETROFIT', text: 'Retrofit'},
                        {value: 'RENTAL', text: 'Rental'},
                        {value: 'EXEMPTION', text: 'Exemption'},
                    ]
                },
                vin: {
                    field: 'vin', type: 'string', value: null, label: 'VIN',
                },
                company_id: {
                    field: 'company_id',
                    type: 'multiselect',
                    search: (query) => {
                        const filters = {
                            rental: {field: 'rental', value: true},
                            name: {field: 'name', value: query}
                        }
                        return Api.getItems(this, 'company', filters)
                    },
                    apiTable: 'company',
                    labelField: 'name',
                    label: 'Rental Company'
                }
            }
        },
    },
    methods: {
        getEntries(listParams) {
            return this.$api.getItems(this, 'equipment', this.filters, listParams)
        },
        getType(item) {
            return item.type ? Equipment.getTypeLabel(item.type) : null
        },
        isAttested(equipment) {
            return equipment.attestationStatus === 'ATTESTED'
        },
        isNoncompliant(equipment) {
            return equipment.compliances[0] && equipment.compliances[0].status === 'NONCOMPLIANT'
        },
        async exportList(filetype, fields) {
            //  context
            const contextTable = this.$refs.dataTable.getContext()
            const context = contextTable ? {sortBy: contextTable.sortBy, perPage: 100000, currentPage: 1} : null
            console.log(context)
            this.$api.getItems(this, 'equipment', this.filters, context, ['userLogs'])
                .then((equipments) => {
                    const callback = Export.equipmentCallback
                    Export.exportRecords(equipments, fields, 'Equipment', filetype, callback)
                })
        },
    }
})
</script>

<style lang="scss">
.equipment-list {
    .heading__buttons {
        display: flex;
        @media (max-width: 767px) {
            flex-direction: column;
            .btn {
                margin: 10px 0 !important;
                width: 100%;
            }
        }
    }
    @media (min-width:1200px) {
        .page-search__filters .col {
            min-width: 250px;
        }
    }
}

</style>
